<template>
  <div class="toolbar-count-row">
    <div class="toolbar-product-count">
      <strong class="product-count-num">{{ nOfProductsFiltered }}</strong
      ><span class="product-count-label">&nbsp;{{ $tc('search.productsFiltered', nOfProductsFiltered) }}</span>
    </div>

    <b-row align-v="center" class="toolbar-chips-row">
      <b-col>
        <cancellable-badge
          v-for="chip in chips"
          :key="chip.id"
          class="active-filter-chip"
          :text="chip.text"
          variant="secondary"
          @cancel="onCancel(chip)"
        ></cancellable-badge>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import CancellableBadge from '../Lib/CancellableBadge'
import { rhpToYears } from '../../utils/formatter'

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('search')

// Same units as RangeFilter.vue's own SLIDER_BOUNDS (RHP in years/months via rhpToYears, the rest
// as percentages) — kept in sync manually since the two components format independently (widget
// hint vs. chip badge).
const RANGE_UNITS = {
  RhpRange: 'years',
  TotalCostsRange: 'percent',
  RiyRange: 'percent',
  InsuredEventRange: 'percent',
  // get_retailInvestorAgeLabel/get_aiDurationPaymentLabel: rhpToYears both sides.
  TMRetailInvestorAge: 'years',
  'AdditionalInfo.PremiumPayedPeriod': 'years',
  // get_limitCapitalLossLevelLabel/get_ai*PercentageLabel/get_aiIntermediaryCostsLabel:
  // Math.round(v * 100) + '%', not $n(v, 'percent').
  TMLimitedCapitalLossLevel: 'roundPercent',
  'AdditionalInfo.AllocationToSegregatedFund': 'roundPercent',
  'AdditionalInfo.AllocationToUnitLinked': 'roundPercent',
  'AdditionalInfo.IntermediaryCosts': 'roundPercent',
  // get_aiPremiumSingleAmountLabel/get_aiPremiumAnnualAmountLabel: $n(v, 'currency').
  'AdditionalInfo.SinglePremiumAmount': 'currency',
  'AdditionalInfo.RecurrentPremiumAmount': 'currency',
  // IC-specific, same DecimalFraction convention as the AdditionalInfo allocations above.
  CapitalProtection: 'roundPercent',
  BarrierLevel: 'roundPercent',
  CouponBarrierLevel: 'roundPercent',
}

// These are literal placeholders — not i18n
// keys, same hardcoded string in both locales — for the side of the range left unset (the slider's
// unbounded extreme), instead of formatting a default numeric value on that side.
const RANGE_NULL_PLACEHOLDERS = {
  RhpRange: { to: '> 20y' },
  TotalCostsRange: { from: '< 0%', to: '> 10%' },
  RiyRange: { from: '< 0%', to: '> 5%' },
  InsuredEventRange: { from: '< 0%', to: '> 5%' },
}

// The rest of the range filters have NO literal placeholder — an unset side just formats
// the slider's own default bound (get_retailInvestorAgeLabel: `tmAgeF == null ? rhpToYears(1) : ...`,
// get_aiPremiumSingleAmountLabel: `... == null ? $n(0, 'currency') : ...`, etc). Mirrors
// RangeFilter.vue's own SLIDER_BOUNDS min/max for the same keys.
const RANGE_DEFAULT_BOUNDS = {
  TMRetailInvestorAge: { min: 1, max: 100 },
  TMLimitedCapitalLossLevel: { min: 0, max: 1 },
  'AdditionalInfo.SinglePremiumAmount': { min: 0, max: 500000 },
  'AdditionalInfo.RecurrentPremiumAmount': { min: 0, max: 10000 },
  'AdditionalInfo.AllocationToSegregatedFund': { min: 0, max: 1 },
  'AdditionalInfo.AllocationToUnitLinked': { min: 0, max: 1 },
  'AdditionalInfo.PremiumPayedPeriod': { min: 1, max: 30 },
  'AdditionalInfo.IntermediaryCosts': { min: 0, max: 1 },
  CapitalProtection: { min: 0, max: 1 },
  BarrierLevel: { min: 0, max: 1 },
  CouponBarrierLevel: { min: 0, max: 1 },
}

// IC-specific RangeDate filters — shown as two independent "Da"/"A" chips instead of one combined
// range badge (see RangeFilter.vue's dedicated RangeDate branch, same Datepicker widget).
const DATE_RANGE_KEYS = ['IssueDate', 'MaturityDate']

// Active-filter chips. The filter set is dynamic (catalog-driven), so labels are resolved via search.filters/search.options
// i18n keys (falling back to the raw filter_key/code when no translation exists), same as
// MultiSelectFilter.vue. Same visual classes (.toolbar-*, CancellableBadge).
// Chips reflect the DRAFT (pendingFilters), not just what was applied,
// one badge per selected value, and removing one re-searches immediately (no need to click "Applica filtri").
export default {
  name: 'activeFilters',
  components: { CancellableBadge },
  props: {
    nOfProductsFiltered: { type: Number, default: 0 },
  },
  computed: {
    ...mapState({
      pendingFilters: (state) => state.pendingFilters,
      catalog: (state) => state.catalog,
    }),
    ...mapGetters(['issuerName']),
    // Chips are always shown in a FIXED order (the default filter object's
    // key order, i.e. the same order the sidebar renders them in) — never in "most recently
    // changed" order. `pendingFilters` here is an array where `withFilterValue` re-appends the
    // touched entry at the end on every edit, so without this the chip order would drift with user
    // interaction instead of staying stable. Sort by each filter's position in the catalog
    // (groups in group_order, filters in filter_order, then the ungrouped tail) to match.
    filterKeyOrder() {
      const groupFilterKeys = (this.catalog.groups || []).flatMap((group) => group.filters.map((f) => f.filterKey))
      const ungroupedKeys = (this.catalog.ungrouped || []).map((f) => f.filterKey)
      return [...groupFilterKeys, ...ungroupedKeys]
    },
    allCatalogFilters() {
      const grouped = (this.catalog.groups || []).flatMap((group) => group.filters)
      return [...grouped, ...(this.catalog.ungrouped || [])]
    },
    orderedPendingFilters() {
      return [...this.pendingFilters].sort((a, b) => {
        const indexA = this.filterKeyOrder.indexOf(a.key)
        const indexB = this.filterKeyOrder.indexOf(b.key)
        return (indexA === -1 ? this.filterKeyOrder.length : indexA) - (indexB === -1 ? this.filterKeyOrder.length : indexB)
      })
    },
    // One badge per selected VALUE — a filter
    // with two selected values in the same dropdown shows as two separate, independently removable chips.
    chips() {
      return this.orderedPendingFilters.flatMap((entry) => {
        // No badge for the free-text search — `searchText` is a separate Vuex
        // state, not part of the filters iterated here.
        if (entry.key === 'SmartFilter') {
          return []
        }
        if (entry.key === 'Perimeter') {
          const text = this.perimeterChipText(entry.value)
          return text ? [{ id: entry.key, key: entry.key, item: null, text }] : []
        }
        if (entry.key === 'CompanyFilter') {
          const text = this.companyFilterChipText(entry.value)
          return text ? [{ id: entry.key, key: entry.key, item: null, text }] : []
        }
        // Date ranges (IssueDate/MaturityDate) — two independent chips ("Da"/"A"), not one combined
        // "Da - A" badge, each removable on its own without clearing the other side.
        if (DATE_RANGE_KEYS.includes(entry.key) && entry.value && typeof entry.value === 'object') {
          return ['from', 'to']
            .filter((side) => entry.value[side] !== null && entry.value[side] !== undefined)
            .map((side) => ({
              id: entry.key + ':' + side,
              key: entry.key,
              rangeSide: side,
              item: null,
              text: this.filterLabel(entry.key) + ' (' + this.$t('search.range' + (side === 'from' ? 'From' : 'To')) + '): ' + this.$d(new Date(entry.value[side])),
            }))
        }
        if (Array.isArray(entry.value)) {
          return entry.value.map((item, index) => ({
            id: entry.key + ':' + index,
            key: entry.key,
            item,
            text: this.filterLabel(entry.key) + ': ' + this.valueLabel(entry.key, item),
          }))
        }
        return [
          {
            id: entry.key,
            key: entry.key,
            item: null,
            text: this.filterLabel(entry.key) + ': ' + this.valueLabel(entry.key, entry.value),
          },
        ]
      })
    },
  },
  methods: {
    ...mapActions(['removeFilter', 'removeFilterValueItem', 'removeFilterRangeSide']),
    onCancel(chip) {
      if (chip.rangeSide) {
        this.removeFilterRangeSide({ key: chip.key, side: chip.rangeSide })
        return
      }
      if (chip.item === null) {
        this.removeFilter(chip.key)
        return
      }
      this.removeFilterValueItem({ key: chip.key, item: chip.item })
    },
    filterLabel(key) {
      const labelKey = 'search.filters.' + key
      return this.$te(labelKey) ? this.$t(labelKey) : key
    },
    // Same as ScoringProfiles/SelectedFilters.vue's perimeter chip ("<date badge>: <date>,
    // <in-perimeter badge>: <label>" shape) instead of the generic
    // "Perimetro: {json}" the composite {mode,...} object would otherwise produce.
    perimeterChipText(value) {
      if (!value || (value.mode !== 'specific_date' && value.mode !== 'window')) {
        return null
      }
      const inPerimeterLabel = value.in_perimeter
        ? this.$te('search.perimeterInPerimeter.' + value.in_perimeter)
          ? this.$t('search.perimeterInPerimeter.' + value.in_perimeter)
          : value.in_perimeter
        : ''
      const inPerimeterPart = inPerimeterLabel
        ? ', ' + this.$t('search.label.perimeterConfiguration.perimeterSdOptions') + ': ' + inPerimeterLabel
        : ''
      if (value.mode === 'specific_date') {
        const dateLabel = value.date ? this.$d(new Date(value.date)) : ''
        return this.$t('search.label.perimeterConfiguration.perimeterSingleDateBadge') + ': ' + dateLabel + inPerimeterPart
      }
      const fromLabel = value.from ? this.$d(new Date(value.from)) : ''
      const toLabel = value.to ? this.$d(new Date(value.to)) : ''
      return this.$t('search.label.perimeterConfiguration.perimeterRangeDateBadge') + ': ' + fromLabel + ' - ' + toLabel + inPerimeterPart
    },
    // Matches CompanyFilterFilter.vue's `selected` computed — the composite {market_products,
    // products_in_test, company} object is only ever "only" or "include" here ("exclude" is the
    // default and stored as a null/absent entry, per withFilterValue), labeled with the same
    // pit.*TestProducts i18n keys the radio buttons themselves use.
    companyFilterChipText(value) {
      if (!value) {
        return null
      }
      const mode = value.market_products === 'none' ? 'only' : value.products_in_test === 'include' ? 'include' : 'exclude'
      const labelKey = mode === 'only' ? 'pit.onlyTestProducts' : mode === 'include' ? 'pit.includeTestProducts' : 'pit.excludeTestProducts'
      return this.filterLabel('CompanyFilter') + ': ' + this.$t(labelKey)
    },
    // Matches MultiSelectFilter.vue's `intervalLabel` — TMTimeHorizonIntervals and
    // AdditionalInfo.RedeemTermIntervals template their labels by the item's position within the
    // catalog's own options array (bucket ids / "Assente" have no stable code of their own).
    intervalItemLabel(key, item) {
      const filter = this.allCatalogFilters.find((f) => f.filterKey === key)
      const options = (filter && filter.options) || []
      const index = options.findIndex((o) => o.from === item.from && o.to === item.to)
      if (key === 'TMTimeHorizonIntervals') {
        const codes = ['1', '2', '4', '8']
        const code = codes[index]
        const n = code === '8' ? item.from + 1 : item.from
        return this.$t('search.filterSelectValues.timeHorizon.' + code, { n, z: item.to })
      }
      return this.$t('search.filterSelectValues.DIP.additionalInformation.aiRedeemTerm.' + index)
    },
    valueLabel(key, value) {
      const orEmpty = (v) => (v === null || v === undefined ? '' : v)
      if (key === 'IssuerCodes') {
        return this.issuerName(value)
      }
      // Boolean filter_type (e.g. IsAutocallable) — same SI/NO labels as the BooleanFilter widget
      // itself, not a raw "true"/"false" string.
      if (typeof value === 'boolean') {
        return this.$t(value ? 'search.booleanTrue' : 'search.booleanFalse')
      }
      if ((key === 'TMTimeHorizonIntervals' || key === 'AdditionalInfo.RedeemTermIntervals') && value && typeof value === 'object') {
        return this.intervalItemLabel(key, value)
      }
      if (value && typeof value === 'object') {
        if ('from' in value || 'to' in value) {
          const unit = RANGE_UNITS[key]
          const placeholders = RANGE_NULL_PLACEHOLDERS[key] || {}
          const defaultBounds = RANGE_DEFAULT_BOUNDS[key] || {}
          const fmt = (v) => {
            if (unit === 'years') return rhpToYears(v)
            if (unit === 'percent') return this.$n(v, 'percent')
            if (unit === 'roundPercent') return Math.round(v * 100) + '%'
            if (unit === 'currency') return this.$n(v, 'currency')
            return v
          }
          // A single-value range selection (from === to, e.g. picking just SRI "4") must show one
          // value, not "4 - 4".
          if (value.from !== null && value.from !== undefined && value.from === value.to) {
            return unit ? fmt(value.from) : String(value.from)
          }
          if (unit) {
            // The side left unset (null) shows either a literal placeholder ("< 0%",
            // "> 5%", "> 20y") for a slider's unbounded extreme, or — for filters with no such
            // placeholder — the formatted default bound (e.g. rhpToYears(1), $n(0, 'currency')),
            // never an empty string (the max side would otherwise disappear entirely).
            const boundKey = { from: 'min', to: 'max' }
            const sideText = (v, side) => {
              if (v !== null && v !== undefined) return fmt(v)
              if (placeholders[side]) return placeholders[side]
              if (boundKey[side] in defaultBounds) return fmt(defaultBounds[boundKey[side]])
              return ''
            }
            return `${sideText(value.from, 'from')} - ${sideText(value.to, 'to')}`
          }
          return `${orEmpty(value.from)} - ${orEmpty(value.to)}`
        }
        return JSON.stringify(value)
      }
      const optionKey = 'search.options.' + key + '.' + value
      return this.$te(optionKey) ? this.$t(optionKey) : String(value)
    },
  },
}
</script>

<style scoped>
/* Own copy of these classes: this component must not depend on another page's global styles. */
.toolbar-count-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  margin-bottom: 10px;
  /* Reserve the height of one chip row so adding/removing the first filter chip doesn't grow/shrink
     this band (the chip is taller than the plain count text) and shove the page a few px down/up.
     Extra chips on the same line don't change height; wrapping to a 2nd line still grows it (expected). */
  min-height: 30px;
}
.toolbar-product-count {
  font-size: 13px;
  color: #6b7280;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
}
.product-count-num {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}
.product-count-label {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}
.toolbar-chips-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

/* CancellableBadge's own `.badge-tag` (scoped) only gives margin/base padding/radius; without
   this, variant="secondary" alone renders a plain gray Bootstrap badge instead of the orange
   "active filter" pill. `!important` on background/color/border only — needed to
   win over Bootstrap's own `.badge-secondary` rules. */
.active-filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px 3px 10px;
  background: #fceae5 !important;
  border: 1px solid #ea633e !important;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #ea633e !important;
  white-space: nowrap;
}
</style>
